import { CRMIntegration } from '@alucio/aws-beacon-amplify/src/models';
import {
  CRMSubmitMeetingPayload,
  CRMSubmitResponseReferences,
  CRMSubmitStandaloneFormPayload,
  FORM_SETTING_TYPE,
  SYNC_STATUS,
} from '../../CRMIndexedDBTypes';
import { ICRMSyncer } from '../../CRMHandler';
import { MeetingORM } from 'src/types/orms';
import { SYNC_ENTRY_REQUEST_TYPE, SYNC_ENTRY_STATUS, SyncEntry } from '../../ISyncManifest';
import { VeevaVaultAPIService } from './VeevaVaultAPIService';
import * as logger from 'src/utils/logger';
import { getTablesSyncEntries } from 'src/state/machines/CRM/util';
import {
  VeevaVaultFormSetting,
  VeevaVaultObjectFieldType,
  VeevaVaultLookups,
  VeevaVaultParentRelationshipFields,
  VeevaVaultUpsertObjectPayload,
} from '../../CRMVeevaVaultTypes';
import {
  handleAttendeesObjectsSubmission,
  handleChildObjectsSubmission,
  handleMainObjectSubmission,
  handleObjectsDeletion,
  handleUnhandledAPIError,
  handleUpsertMainObjects, handleUpsertStandaloneFormRecordsFromMeeting, handleDeleteStandaloneFormRecords,
  structureVeevaVaultFormSettingsPicklists,
  UpsertObjectsResult,
} from './VeevaVaultSyncerUtils';
import { MEETING_SUBMIT_TYPE } from 'src/state/context/Meetings/MeetingSubmissionTypes';
import { getAttendeesMapped } from '../../Translators/VeevaFormTranslator';
import { buildUpsertObjectRecord } from '../../Translators/VeevaVault/VeevaVaultTranslatorUtils';

export class VeevaVaultSyncer implements ICRMSyncer {
  readonly config: CRMIntegration = {} as CRMIntegration
  readonly apiService: VeevaVaultAPIService;

  constructor(config: CRMIntegration) {
    if (!config) throw new Error('CRM Integration is not configured')
    if (!config.instanceUrl) throw new Error('CRM Instance URL is not configured')
    this.config = config
    this.apiService = new VeevaVaultAPIService();
  }

  initialize(): SyncEntry[] {
    const meetingSettings = this.config.meetingSetting;

    if (!meetingSettings && !this.config.crmStandaloneForms?.length) {
      return [];
    }

    return getTablesSyncEntries(this.config, '');
  }

  async processManifestEntry(syncEntry: SyncEntry): Promise<SyncEntry[]> {
    const entries: SyncEntry[] = [];

    switch (syncEntry.subType) {
      case SYNC_ENTRY_REQUEST_TYPE.TABLE: {
        const tableInfo = await this.apiService.getObjectLayouts(syncEntry);
        entries.push(...tableInfo);
        break;
      }
      case SYNC_ENTRY_REQUEST_TYPE.PICKLIST: {
        const picklistInfo = await this.apiService.getPicklists(syncEntry);
        entries.push(picklistInfo);
        break;
      }

      case SYNC_ENTRY_REQUEST_TYPE.LOOKUP: {
        const lookupInfo = await this.apiService.getLookup(syncEntry);
        entries.push(lookupInfo);
        break;
      }

      default:
        logger.veevaVaultSyncer.warn(`No handler for ${syncEntry.subType} sync entry type`, { syncEntry });
        entries.push({
          ...syncEntry,
          status: SYNC_ENTRY_STATUS.BYPASSED_ERROR,
        });
    }

    return entries;
  }

  async configSyncComplete(entries: SyncEntry[]): Promise<VeevaVaultFormSetting[]> {
    const results: VeevaVaultFormSetting[] = [];
    const meetingSettings = this.config.meetingSetting;
    const standaloneForms = this.config.crmStandaloneForms;
    const mainObjectAPIName = this.config.meetingSetting?.apiName;

    if (!meetingSettings) {
      return results;
    }

    const tables: SyncEntry[] = [];
    const picklists: SyncEntry[] = [];
    const lookups: SyncEntry[] = [];

    entries.forEach((entry) => {
      if (entry.status === SYNC_ENTRY_STATUS.SYNCED) {
        switch (entry.subType) {
          case SYNC_ENTRY_REQUEST_TYPE.LOOKUP:
            lookups.push(entry);
            break;
          case SYNC_ENTRY_REQUEST_TYPE.RECORD_TYPE:
            tables.push(entry);
            break;
          case SYNC_ENTRY_REQUEST_TYPE.PICKLIST:
            picklists.push(entry);
            break;
        }
      }
    });

    tables.forEach((entryTable) => {
      const { id, data, parameters } = entryTable;
      const { layout, objectInfo } = data;
      const apiName = objectInfo.name;
      const isMainTable = meetingSettings.apiName === apiName;

      const isStandaloneForm = parameters.isStandaloneForm;
      let parentRelationshipFieldName = '';

      if (isStandaloneForm) {
        standaloneForms?.forEach((form) => {
          form.parents?.forEach((parent) => {
            if (apiName === form.apiName && parent.apiName === mainObjectAPIName) {
              parentRelationshipFieldName = parent.relationshipName;
            }
          });
        });
      }

      const queriedLookups = new Set(entries
        .filter(entry => entry.subType === SYNC_ENTRY_REQUEST_TYPE.LOOKUP &&
          entry.parameters.parentId === apiName && entry.parameters.query)
        .map(entry => entry.parameters.lookupApiName));

      const tableLookups: VeevaVaultLookups = {};
      const objectLookups: VeevaVaultLookups = {};
      lookups.forEach(({ data, parameters }) => {
        if (parameters.parentId === apiName) {
          const target = parameters.query ? tableLookups : objectLookups;
          target[parameters.lookupApiName || ''] = data;
        }
      });

      objectInfo.fields.forEach((field) => {
        const lookupObject = field.object?.name;
        if (field.type === VeevaVaultObjectFieldType.OBJECT && lookupObject &&
          !queriedLookups.has(field.name) && objectLookups[lookupObject]) {
          tableLookups[field.name] = objectLookups[lookupObject];
        }
      });

      const picklistSyncEntry = picklists.find((entry) =>
        entry.parameters.apiName === apiName &&
        entry.parameters.recordTypeId === parameters.recordTypeId);

      const structuredPicklists = structureVeevaVaultFormSettingsPicklists(layout, objectInfo, picklistSyncEntry);
      let label: string = objectInfo.label;

      const hasMoreObjectTypes = tables.filter((syncEntry) =>
        syncEntry.subType === SYNC_ENTRY_REQUEST_TYPE.RECORD_TYPE &&
        syncEntry.parameters.apiName === apiName).length > 1;

      if (hasMoreObjectTypes) {
        const objectTypeLabel = objectInfo.object_types?.find((type) => type.name === parameters.recordTypeId)?.label;
        if (objectTypeLabel) {
          label += ` - ${objectTypeLabel}`;
        }
      }

      const formSetting: VeevaVaultFormSetting = {
        id,
        apiName,
        isMainTable,
        objectType: parameters.recordTypeId,
        layoutLabel: layout.label,
        label,
        objectInfo,
        layout,
        picklists: structuredPicklists,
        lookups: tableLookups,
        parentTableRelationshipName: parentRelationshipFieldName || VeevaVaultParentRelationshipFields[apiName],
        formSettingType: FORM_SETTING_TYPE.VEEVA_VAULT,
        isStandaloneForm,
      };

      results.push(formSetting);
    });

    return results;
  }

  async handleDeleteMeeting(meeting: MeetingORM): Promise<CRMSubmitResponseReferences> {
    logger.veevaVaultSyncer.info('Delete meeting in CRM is called...');
    const meetingId = meeting.model.crmRecord?.crmCallId;
    const apiName = this.config.meetingSetting?.apiName;

    if (!meetingId) {
      logger.veevaVaultSyncer.warn('External Id not found...');
      return {};
    } else if (!apiName) {
      logger.veevaVaultSyncer.warn('Main record API name not found...');
      return {};
    }

    analytics.track('MEETING_DELETE', {
      category: 'MEETING',
      type: 'DELETE',
      integration: 'VEEVA_VAULT',
      crmRecordId: meetingId,
    });

    return this.apiService.deleteVeevaVaultObjects(apiName, [{ beaconId: meeting.model.id, externalId: meetingId }]);
  }

  async submitStandaloneFormToCRM(crmSubmitPayload: CRMSubmitStandaloneFormPayload):
    Promise<CRMSubmitResponseReferences> {
    logger.veevaVaultSyncer.info('StandaloneForm Submit is called...', crmSubmitPayload);
    const isFirstSubmission = !crmSubmitPayload.recordFormORM.model.crmFields?.externalId;
    return isFirstSubmission ? this.handleStandaloneFormSubmit(crmSubmitPayload) : {};
  }

  private async handleStandaloneFormSubmit(crmSubmitPayload: CRMSubmitStandaloneFormPayload):
    Promise<CRMSubmitResponseReferences> {
    const { standaloneForm, recordFormORM } = crmSubmitPayload;

    if (!standaloneForm.veevaCRMFormSetting) {
      throw Error('Settings for main table not found.');
    }

    const payload = await buildUpsertObjectRecord({
      objectFormSetting: standaloneForm.veevaCRMFormSetting,
      crmValues: recordFormORM.meta.formValues,
      referenceId: recordFormORM.model.id,
      objectType: standaloneForm.veevaCRMFormSetting.objectType,
      isSubmit: crmSubmitPayload.isSubmit,
    });

    let response: CRMSubmitResponseReferences = {};

    try {
      response = await this.apiService.upsertVeevaVaultObjects(standaloneForm.veevaCRMFormSetting.apiName, [payload]);
    } catch (e: any) {
      logger.veevaVaultSyncer.error('Failed to submit the CRM standalone form...', e);
      return handleUnhandledAPIError(e);
    }

    return response;
  }

  async submitToCRM(crmSubmitPayload: CRMSubmitMeetingPayload): Promise<CRMSubmitResponseReferences> {
    logger.veevaVaultSyncer.info('Submit to CRM is called...');
    const mainFormSetting = crmSubmitPayload.veevaVaultFormSettings.find((setting) => setting.isMainTable);

    if (!mainFormSetting) {
      throw Error('Settings for main table not found.');
    }

    let result: CRMSubmitResponseReferences = {};

    if (crmSubmitPayload.mainCrmRecordId) {
      result = await this.handleUpdates(crmSubmitPayload, mainFormSetting);
    } else {
      result = await this.handleFirstSubmit(crmSubmitPayload, mainFormSetting);
    }

    // IF WE'RE SUBMITTING FINAL, WE FIRST UPDATE THE OBJECTS AND THEN WE UPDATE THEIR STATUS
    if (crmSubmitPayload.submitType === MEETING_SUBMIT_TYPE.SUBMIT_LOCK_TO_CRM) {
      return this.handleFinalSubmit(crmSubmitPayload, mainFormSetting, result);
    }

    return result;
  }

  private async handleFirstSubmit(
    crmSubmitPayload: CRMSubmitMeetingPayload,
    mainFormSetting: VeevaVaultFormSetting): Promise<CRMSubmitResponseReferences> {
    logger.veevaVaultSyncer.info('Handling first submit to CRM...');

    // 1. GET THE ATTENDEES WITH THEIR CRM ACCOUNTS
    const attendeesMapped = await getAttendeesMapped(crmSubmitPayload, crmSubmitPayload.formValuesAttendees);
    logger.veevaVaultSyncer.info('Attendees mapped with CRM Accounts: ', attendeesMapped);

    // 2. CREATES THE MAIN OBJECT
    const mainObjectResponse = await handleMainObjectSubmission(
      crmSubmitPayload, mainFormSetting, attendeesMapped, this.apiService.upsertVeevaVaultObjects);
    const mainObjectVeevaVaultId = mainObjectResponse.response['main-record']?.externalId;

    if (!mainObjectVeevaVaultId) {
      logger.veevaVaultSyncer.error('Failed to create main record in Veeva Vault', { mainObjectResponse });
      return mainObjectResponse.response;
    }

    // 2.1. QUERY THE NAME OF THE NEWLY CREATED RECORD
    const mainObjectName = await this.apiService.queryRecordName(mainFormSetting.apiName, mainObjectVeevaVaultId);
    if (mainObjectName && mainObjectResponse.response['main-record']) {
      mainObjectResponse.response['main-record'].externalName = mainObjectName;
    }

    // 3. CREATES THE ATTENDEES/USERS CALL RECORDS
    const attendeeObjectsResponse = await handleAttendeesObjectsSubmission(
      crmSubmitPayload, mainFormSetting, mainObjectVeevaVaultId, mainObjectResponse.objects[0].object,
      attendeesMapped, this.apiService.upsertVeevaVaultObjects);

    const unitedResponseCreations: UpsertObjectsResult = {
      objects: [...attendeeObjectsResponse.objects, ...mainObjectResponse.objects],
      response: { ...mainObjectResponse.response, ...attendeeObjectsResponse.response },
    };

    // 4. CREATES THE CHILD RECORDS OF THE MAIN CALL AND ATTENDEES CALLS
    const childObjectsResponse = await handleChildObjectsSubmission(
      attendeesMapped, crmSubmitPayload, unitedResponseCreations, this.apiService.upsertVeevaVaultObjects);

    // 5. CREATES THE STANDALONE FORM RECORDS
    const standaloneRecordsResponse = await handleUpsertStandaloneFormRecordsFromMeeting(
      crmSubmitPayload, mainObjectVeevaVaultId, this.apiService.upsertVeevaVaultObjects);

    return {
      ...mainObjectResponse.response,
      ...attendeeObjectsResponse.response,
      ...childObjectsResponse.response,
      ...standaloneRecordsResponse,
    };
  }

  private async handleUpdates(crmSubmitPayload: CRMSubmitMeetingPayload,
    mainFormSetting: VeevaVaultFormSetting): Promise<CRMSubmitResponseReferences> {
    logger.veevaVaultSyncer.info('Handling updating to CRM...');

    // 1. GET THE ATTENDEES WITH THEIR CRM ACCOUNTS
    const attendeesMapped = await getAttendeesMapped(crmSubmitPayload, crmSubmitPayload.formValuesAttendees);
    logger.veevaVaultSyncer.info('Attendees mapped with CRM Accounts: ', attendeesMapped);

    // 2. UPDATES THE EXISTING MAIN OBJECTS AND CREATES THE ONES FOR THE NEW ATTENDEES
    const mainObjectsResponse = await handleUpsertMainObjects(
      crmSubmitPayload, mainFormSetting, attendeesMapped, this.apiService.upsertVeevaVaultObjects);

    // 2.1. BACKFILL THE MAIN RECORD NAME FOR LEGACY/PRE-EXISTING SYNCED MEETINGS THAT NEVER STORED IT.
    // SAFE BECAUSE THE RECORD IS ALREADY SYNCED (KNOWN GOOD ID) AND IT'S A ONE-TIME COST PER MEETING.
    const mainRecordResponse = mainObjectsResponse.response['main-record'];
    const existingCrmCallName = crmSubmitPayload.meetingORM.model.crmRecord?.crmCallName;
    if (
      mainRecordResponse &&
      mainRecordResponse.syncStatus === SYNC_STATUS.SYNCED &&
      mainRecordResponse.externalId &&
      !existingCrmCallName &&
      !mainRecordResponse.externalName
    ) {
      const mainObjectName = await this.apiService.queryRecordName(
        mainFormSetting.apiName, mainRecordResponse.externalId);
      if (mainObjectName) {
        mainRecordResponse.externalName = mainObjectName;
      }
    }

    // 3. UPDATES THE EXISTING CHILD OBJECTS AND CREATES THE ONES FOR THE NEW ATTENDEES
    const childObjectsResponse = await handleChildObjectsSubmission(
      attendeesMapped, crmSubmitPayload, mainObjectsResponse, this.apiService.upsertVeevaVaultObjects);

    // 4. DELETE THE CALL RECORDS OF THE REMOVED ATTENDEES AND CHILD OBJECTS
    const deletedObjectsResponse = await handleObjectsDeletion(
      crmSubmitPayload, childObjectsResponse.response,
      mainFormSetting.apiName, this.apiService.deleteVeevaVaultObjects);

    // 5. UPSERTS (CREATES AND UPDATES) THE STANDALONE FORM RECORDS
    const standaloneRecordsResponse = await handleUpsertStandaloneFormRecordsFromMeeting(
      crmSubmitPayload, mainObjectsResponse.response['main-record']?.externalId || '',
      this.apiService.upsertVeevaVaultObjects);

    // 6. DELETES THE STANDALONE FORM RECORDS THAT WERE REMOVED
    const deletedStandaloneRecordsResponse = await handleDeleteStandaloneFormRecords(
      crmSubmitPayload, this.apiService.deleteVeevaVaultObjects);

    return {
      ...mainObjectsResponse.response,
      ...childObjectsResponse.response,
      ...deletedObjectsResponse,
      ...standaloneRecordsResponse,
      ...deletedStandaloneRecordsResponse,
    };
  }

  private async handleFinalSubmit(crmSubmitPayload: CRMSubmitMeetingPayload,
    mainFormSetting: VeevaVaultFormSetting, objectsResponseReference: CRMSubmitResponseReferences,
  ): Promise<CRMSubmitResponseReferences> {
    logger.veevaVaultSyncer.info('Handling locking the calls in CRM...');
    const apiName = mainFormSetting.apiName;
    const upserts: VeevaVaultUpsertObjectPayload[] = [];
    const upsertedObjectsKeys = Object.keys(objectsResponseReference);
    let hasErrors: boolean = false;

    for (const beaconId of upsertedObjectsKeys) {
      if (objectsResponseReference[beaconId].syncStatus === SYNC_STATUS.ERROR) {
        hasErrors = true;
        break;
      }

      const shouldLockRecord = crmSubmitPayload.formValuesAttendees.some(
        (formAttendee) => formAttendee.id === beaconId);
      const externalId = objectsResponseReference[beaconId].externalId;

      if (shouldLockRecord && externalId) {
        upserts.push({
          referenceId: beaconId,
          objectAPIName: apiName,
          object: {
            id: externalId,
            call2_status__v: 'submitted__v',
          },
        });
      }
    }

    const mainRecordExternalId = objectsResponseReference['main-record']?.externalId;

    if (!mainRecordExternalId ||
      hasErrors ||
      objectsResponseReference['main-record']?.syncStatus === SYNC_STATUS.ERROR) {
      return objectsResponseReference;
    }

    upserts.push({
      referenceId: 'main-record',
      objectAPIName: apiName,
      object: {
        id: mainRecordExternalId,
        call2_status__v: 'submitted__v',
      },
    });

    const responsesFromLocking =
      await this.apiService.upsertVeevaVaultObjects(apiName, upserts, 'id');

    return {
      ...objectsResponseReference,
      ...responsesFromLocking,
    };
  }
}
